import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { environment } from '../../../../environments/environment';
import { IAsyncCall } from '../../../analytics/models/i-async-call.interface';
import { IEcapsState } from '../../../analytics/models/i-ecaps-state.interface';
import { ProjectsService } from '../../../projects/services/projects.service';
import { LayoutService } from '../../../selections/services/layout.service';
import { UsersService } from '../../../users/services/users.service';

export enum methods {
  post = 'POST',
  get = 'GET',
  put = 'PUT',
  delete = 'DELETE',
}

@Injectable({
  providedIn: 'root',
})
export class EcapsStateService {
  private pendingAsyncCalls: IAsyncCall[] = [];

  layoutService: LayoutService;
  projectsService: ProjectsService;
  usersService: UsersService;

  constructor() {}

  addPendingAsyncCall(
    url: string,
    method: methods | string,
    data?: any
  ): string {
    const key = uuid();

    this.pendingAsyncCalls.push({
      key: key,
      url: url,
      method: method,
      data: data,
    });

    return key;
  }

  removePendingAsyncCall(key: string) {
    const index = this.pendingAsyncCalls.findIndex((item) => item.key === key);

    if (index > -1) {
      this.pendingAsyncCalls.splice(index, 1);
    }
  }

  getCurrentState(): Promise<IEcapsState> {
    return new Promise<IEcapsState>(async (result) => {
      const state: IEcapsState = {
        environment: environment.environment,
        version: environment.version,
        layoutConfig: null,
        project: null,
        userData: null,
        localStorage: window.localStorage,
        currentUrl: window.location.pathname,
        pendingAsyncCalls: this.pendingAsyncCalls,
      };

      if (!!this.layoutService) {
        try {
          state.layoutConfig = await this.layoutService.getLastLayoutConfig();
        } catch {
          state.layoutConfig = null;
        }
      }

      if (!!this.projectsService) {
        try {
          const project = await this.projectsService.getLocalProject();

          if (!!project) {
            state.project = project.toJSON();

            const activeRevision = project.revisions.find(
              (revision) => revision.active
            );

            const stateRevision = activeRevision.toJSON();

            state.project.revisions = [stateRevision];

            stateRevision.items = activeRevision.items.map<any>((item) => {
              const {
                areaServed,
                details,
                id,
                location,
                name,
                notes,
                quantity,
                tag,
                type,
                version,
              } = item;

              const jsonMap = {
                areaServed,
                details,
                id,
                location,
                name,
                notes,
                quantity,
                tag,
                type,
                version,
                sizeData: undefined,
              };

              if (!!item.sizeData) {
                const {
                  id,
                  model,
                  name,
                  selectionLayoutConfig,
                  selectionKey,
                  layoutConfig,
                } = item.sizeData;

                jsonMap.sizeData = {
                  id,
                  model,
                  name,
                  selectionLayoutConfig,
                  selectionKey,
                  layoutConfig,
                };
              }

              return jsonMap;
            });
          }
        } catch {
          state.project = null;
        }
      }

      if (!!this.usersService) {
        try {
          state.userData = await this.usersService.getCurrentUser(false);
        } catch {
          state.userData = null;
        }
      }

      result(state);
    });
  }
}
